import { useCallback, useMemo } from 'react'

import { KeyedMutator, SWRConfiguration } from 'swr'

import { ChargifySubscriptionResponse, Product, Subscription, TierOffering } from '@infura/packages.api'
import { getUserChargifySubscriptionReference } from '@infura/packages.common/user'

import useUser from 'hooks/useUser'

import { useApi } from 'lib/api/api'
import { getChargifyBilling, isSubscriptionInValid } from 'lib/utils/billing'

interface SubscriptionData {
  subscription: Subscription
  isSubscriptionCanceling: boolean
  subscriptionCancelDate: number
  isTierExpiring?: boolean
  isAddonExpiring?: () => boolean
  newTier?: TierOffering
  loading: boolean
  mutate: KeyedMutator<ChargifySubscriptionResponse>
}

const defaultConfig = { errorRetryCount: 0, shouldRetryOnError: false }

export default function useSubscription(config?: SWRConfiguration): SubscriptionData {
  const { user } = useUser()
  // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
  const billingInfo = useMemo(() => getChargifyBilling(user, Product.ETHEREUM), [user])

  const swrConfig = useMemo(() => ({ ...defaultConfig, ...config }), [config])
  const {
    data: subscription,
    error: subscriptionError,
    isLoading: subscriptionLoading,
    ...swr
  } = useApi(['getSubscription'], swrConfig)
  const {
    data: allSubscriptions,
    error: allSubscriptionsError,
    isLoading: subscriptionsLoading
  } = useApi(['listSubscriptions'], swrConfig)

  // For some unknown reasons, the Chargify subscription lookup and Read subscription apis aren't reliable
  // i.e the don't always return a response. Hence I'm relying on the list Subscriptions api
  // to get the active sub. (Try on postman: https://infura.postman.co/workspace/Pricing-v2~b63bf888-282b-4d07-b356-c406b87a0cae/request/25545676-0f8b6beb-a492-4a2e-8e27-af75259ea731?ctx=documentation)
  const activeSubscription = useMemo(() => {
    // Verify the existence of subscription data, or the absence of both billing information and all subscriptions
    // In such cases, return the subscription data if available, otherwise return null
    // This ensures that we don't return {data: null} when `subscription` is non-false but contains null, especially when `allSubscriptions` has entries
    if ((subscription && subscription.data !== null) || !billingInfo || !allSubscriptions)
      return subscription?.data || null
    // The user's unique subscription reference ensures retrieval of the most recent subscription data, independent of payment profile id stored on the invoice.
    // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
    const userChargifySubscriptionReference = getUserChargifySubscriptionReference(user)
    const active = allSubscriptions?.data?.find(item => {
      return item.subscription.reference === userChargifySubscriptionReference
    })
    return !active?.subscription || isSubscriptionInValid(active?.subscription) ? null : active?.subscription
  }, [allSubscriptions, subscription, subscriptionLoading, subscriptionsLoading, user])

  const isAddonExpiring = useCallback(() => {
    return activeSubscription?.cancel_at_end_of_period
  }, [activeSubscription])

  return {
    // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
    isAddonExpiring,
    // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
    isSubscriptionCanceling: activeSubscription?.cancel_at_end_of_period,
    // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
    isTierExpiring: activeSubscription?.cancel_at_end_of_period,
    loading:
      !activeSubscription &&
      !subscriptionError &&
      !allSubscriptions &&
      !allSubscriptionsError &&
      !subscriptionLoading &&
      !subscriptionsLoading,
    mutate: swr.mutate,
    // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
    newTier: null,
    // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
    subscription: activeSubscription,
    subscriptionCancelDate: Number(activeSubscription?.canceled_at || '')
  }
}
